import { useAtomValue } from "jotai";
import { User } from "oidc-client-ts";
import { useCallback, useEffect, useRef, useState } from "react";

import { prefetchAccess } from "~/access/payload";
import {
  type AuthProfile,
  authProfileAtom,
  orgsAtom,
  store,
  userAtom,
  userConfigAtom,
  workosAuthAtom,
  zitadelOrgAtom,
} from "~/atoms";
import { createAxiosRequest } from "~/hooks/useApiQuery";
import { markBuildBooted } from "~/lib/appBuild";
import { isWorkOSAuth } from "~/lib/authProvider";
import {
  applyOrgSelection,
  fetchUserConfig,
  fetchUserOrgs,
  profileFromZitadelUser,
} from "~/lib/orgSelection";
import { actAfterLoginSuccess } from "~/utils/Helper";
import { Org } from "~/utils/Types";

export type AppBootstrapStatus =
  | "idle"
  | "loading"
  | "ready"
  | "needsOrgSelection"
  | "error";

export interface AppBootstrapState {
  status: AppBootstrapStatus;
  error?: Error;
  orgsAwaitingSelection?: Org[];
  selectOrg: (orgIdString: string) => Promise<void>;
}

/**
 * Mongo User shape returned by POST /user/UserInfo (per the v2 contract;
 * see v2-server commit b20c5bd `feat(api): return raw user object from
 * GET /user/info`). Only the fields useAppBootstrap reads are declared
 * here — the SPA does not consume the full document directly. WorkOS
 * path only; the Zitadel path takes its profile from the OIDC User.
 */
interface UserInfo {
  _id?: string;
  workosUserId?: string | null;
  email?: string;
  firstName?: string;
  lastName?: string;
  super?: "super-admin" | "super-user";
}

/**
 * Synthesize a Zitadel-shape `User` from the v2 `/user/UserInfo` payload
 * for the WorkOS path. Existing consumers of `userAtom` (Nav-internal
 * sub-components passing `user` to `<SwitchRole>`/`<SwitchOrg>`,
 * `useAxios` fallbacks, etc.) keep working without their needing to
 * know about WorkOS.
 *
 * NOTE — `access_token` is deliberately `""` on the WorkOS path. The SPA
 * authenticates via the meq_at + meq_rt cookies on .meqinsights.com; the
 * JWT must not be exposed to JS for XSS-resistance. `createAxiosRequest`
 * gates `Authorization` off on the WorkOS path so the empty string is
 * never sent.
 */
function synthesizeUserFromUserInfo(info: UserInfo): User {
  const firstName = info.firstName ?? "";
  const lastName = info.lastName ?? "";
  const name = `${firstName} ${lastName}`.trim();
  return {
    access_token: "",
    token_type: "Bearer",
    session_state: null,
    profile: {
      sub: info.workosUserId ?? info._id ?? "",
      name,
      given_name: firstName,
      family_name: lastName,
      email: info.email,
      // Custom claim (not OIDC): carried on the synthesized profile so every
      // re-derivation of authProfileAtom from userAtom preserves it. `sub`
      // stays the WorkOS id — never fold _id into it.
      mongoId: info._id,
    },
    state: undefined,
    expired: false,
    scopes: [],
    expires_in: undefined,
  } as unknown as User;
}

function profileFromWorkosUserInfo(info: UserInfo): AuthProfile {
  const firstName = info.firstName ?? "";
  const lastName = info.lastName ?? "";
  const displayName = `${firstName} ${lastName}`.trim();
  return {
    id: info.workosUserId ?? info._id ?? "",
    email: info.email ?? "",
    firstName,
    lastName,
    displayName,
    mongoId: info._id,
  };
}

async function fetchUserInfo(): Promise<UserInfo> {
  return createAxiosRequest<UserInfo>({
    config: { method: "post", data: {} },
    path: "/user/UserInfo",
    transformers: [],
    user: null,
    zitadelOrg: null,
  });
}

/**
 * Strip the one-shot `?authUser=&orgId=` query params authkit-ui appends
 * on the first redirect into the SPA. After we hydrate the atoms from
 * the API we don't want the JSON-encoded user blob lingering in the URL
 * (shareable links, browser history, etc.). WorkOS path only.
 */
const HANDOFF_PARAMS = ["authUser", "orgId"] as const;

function cleanupAuthQueryParams(): void {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  if (!HANDOFF_PARAMS.some((param) => url.searchParams.has(param))) {
    return;
  }
  HANDOFF_PARAMS.forEach((param) => url.searchParams.delete(param));
  const next = url.pathname + (url.search ? url.search : "") + url.hash;
  window.history.replaceState(window.history.state, "", next);
}

/**
 * True when the Zitadel OIDC user carries an access token that is present
 * and not expired. `userAtom` is `atomWithStorage` (getOnInit), so every
 * load synchronously rehydrates the *previous* session's token — which may
 * already be stale. Gating the boot on freshness (rather than mere
 * presence) stops the bootstrap firing `UserOrgs` with a dead token (→ 401
 * → "Failed to load organizations") before `signinRedirectCallback` has
 * installed the fresh token. `expires_at` is epoch seconds; when it's
 * absent we optimistically allow the token and let the server + the 401
 * self-heal in the boot effect be the backstop.
 */
function hasFreshZitadelToken(user: User | undefined): boolean {
  if (!user?.access_token) return false;
  if (user.expires_at == null) return true;
  return user.expires_at * 1000 > Date.now();
}

const IDLE_STATE: Pick<AppBootstrapState, "status"> = { status: "idle" };
const LOADING_STATE: Pick<AppBootstrapState, "status"> = { status: "loading" };
const READY_STATE: Pick<AppBootstrapState, "status"> = { status: "ready" };

/**
 * Boots the SPA after auth. Auth-flow-agnostic by design — detects the
 * active path via `isWorkOSAuth()`:
 *
 * - **WorkOS path:** profile via `POST /user/UserInfo`; orgs and config via
 *   `POST /user/UserOrgs|UserConfig` and the access payload via
 *   `GET /me/access`, with the meq_at cookie. Single-org users boot straight to `ready`; multi-org
 *   users surface `needsOrgSelection` with the candidate orgs list, and
 *   the caller drives the choice via `selectOrg`.
 *
 * - **Zitadel path:** profile comes from the OIDC `User.profile` already
 *   in `userAtom` (no UserInfo round trip). Same downstream sequence.
 *
 * Both branches populate `authProfileAtom` AND call
 * `actAfterLoginSuccess` so consumers reading `userAtom`, `orgsAtom` and
 * `userConfigAtom` keep working.
 *
 * On reload, if the atoms are already populated (atomWithStorage
 * hydrates synchronously at module init) the hook short-circuits to
 * `ready` without firing any network calls — and re-derives
 * `authProfileAtom` from `userAtom` so in-memory display state is
 * restored.
 */
/**
 * Options controlling bootstrap behaviour when atoms are already
 * populated from a prior session (atomWithStorage hydration).
 *
 * - `forceRefetch: false` (default, used by PrivateRoutes for /main):
 *   if all three session atoms are populated, short-circuit to "ready"
 *   without firing the API sequence. Preserves the user's selected org
 *   across navigations + page reloads of protected routes.
 *
 * - `forceRefetch: true` (used by Login at /): ignore cached atoms;
 *   always run the bootstrap sequence. Multi-org users get re-prompted
 *   via the SelectOrg dialog every time they land on /, matching the
 *   pre-commit-3 Zitadel UX.
 */
export interface UseAppBootstrapOptions {
  forceRefetch?: boolean;
}

export function useAppBootstrap(
  options: UseAppBootstrapOptions = {},
): AppBootstrapState {
  const { forceRefetch = false } = options;
  const workosAuth = useAtomValue(workosAuthAtom);
  const orgs = useAtomValue(orgsAtom);
  const userConfig = useAtomValue(userConfigAtom);
  const user = useAtomValue(userAtom);

  // The boot is gated on a *usable* credential, not mere presence: WorkOS
  // uses the in-memory session user; Zitadel requires a non-expired access
  // token (hasFreshZitadelToken). `tokenKey` identifies that credential so
  // the boot re-runs when a fresh token replaces a stale one (e.g. after
  // signinRedirectCallback / silent renew) instead of being latched.
  const workos = isWorkOSAuth();
  const triggerReady = workos ? !!workosAuth.user : hasFreshZitadelToken(user);
  const tokenKey = workos
    ? (workosAuth.user?.id ?? null)
    : (user?.access_token ?? null);

  const [state, setState] = useState<AppBootstrapState>(() => {
    if (!triggerReady) return { ...IDLE_STATE, selectOrg: noopSelectOrg };
    if (!forceRefetch && orgs && userConfig && user) {
      return { ...READY_STATE, selectOrg: noopSelectOrg };
    }
    return { ...LOADING_STATE, selectOrg: noopSelectOrg };
  });
  // Which credential the boot last attempted, so a *new* token re-runs the
  // sequence while the same token (re-written by actAfterLoginSuccess) does
  // not loop. Replaces the old boolean `attempted` guard.
  const attemptedTokenRef = useRef<string | null>(null);
  // Cache the orgs surfaced in needsOrgSelection so the closure-captured
  // `selectOrg` doesn't go stale across re-renders.
  const pendingOrgsRef = useRef<Org[] | null>(null);

  // ---------------- selectOrg (multi-org) ----------------

  const selectOrg = useCallback(async (orgIdString: string): Promise<void> => {
    const pendingOrgs = pendingOrgsRef.current;
    if (!pendingOrgs || pendingOrgs.length === 0) return;

    try {
      await applyOrgSelection({ orgIdString, orgs: pendingOrgs });
      pendingOrgsRef.current = null;
      setState({ ...READY_STATE, selectOrg });
    } catch (err) {
      setState({
        status: "error",
        error: err instanceof Error ? err : new Error(String(err)),
        selectOrg,
      });
    }
  }, []);

  // ---------------- boot effect ----------------

  useEffect(() => {
    if (!triggerReady) {
      setState({ ...IDLE_STATE, selectOrg });
      attemptedTokenRef.current = null;
      pendingOrgsRef.current = null;
      return;
    }

    // Atoms already populated (page reload, atomWithStorage hydration).
    // Restore authProfileAtom from userAtom so Nav/etc. have display
    // state without a network round trip. Short-circuit ONLY when
    // forceRefetch is false (i.e. invoked from a protected route);
    // Login at / passes forceRefetch=true so multi-org users always
    // get re-prompted via the SelectOrg dialog there.
    if (!forceRefetch && orgs && userConfig && user) {
      if (!store.get(authProfileAtom)) {
        store.set(authProfileAtom, profileFromZitadelUser(user));
      }
      setState({ ...READY_STATE, selectOrg });
      return;
    }

    if (attemptedTokenRef.current === tokenKey) return;
    attemptedTokenRef.current = tokenKey;
    setState({ ...LOADING_STATE, selectOrg });
    // Fetching from scratch on this build — nothing cached can be stale.
    markBuildBooted();

    void (async () => {
      try {
        if (workos) {
          await bootWorkos({ selectOrg, setState, pendingOrgsRef });
        } else {
          await bootZitadel({ selectOrg, setState, pendingOrgsRef });
        }
      } catch (err) {
        attemptedTokenRef.current = null;
        // Surface the failure — including its HTTP status when it's a
        // ResponseError — so the caller can act on it. Login re-authenticates
        // via the Zitadel userManager on a 401 (stale/rotated/corrupt token);
        // other errors just render the error state.
        setState({
          status: "error",
          error: err instanceof Error ? err : new Error(String(err)),
          selectOrg,
        });
      }
    })();
    // React to the auth-flow trigger AND the credential identity: a fresh
    // token (post-callback / silent renew) must re-run the boot. orgs/
    // userConfig/user are read inside for the "already populated"
    // short-circuit but must NOT be deps — actAfterLoginSuccess writes
    // them, which would re-fire the effect. It re-writes userAtom with the
    // SAME access_token, so `tokenKey` is unchanged and does not loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerReady, tokenKey]);

  return state;
}

// Placeholder used as the selectOrg field on the initial idle/loading
// state before the real callback is bound. Resolves immediately; the
// real selectOrg replaces it once the hook is mounted.
const noopSelectOrg = async (_orgIdString: string): Promise<void> => {
  // intentionally empty
};

// ---------------- branch implementations ----------------

type BootCtx = {
  selectOrg: (orgIdString: string) => Promise<void>;
  setState: (s: AppBootstrapState) => void;
  pendingOrgsRef: { current: Org[] | null };
};

/**
 * Orgs the boot-time picker offers. authkit-ui authenticates against one
 * customer-level WorkOS org, so a user who belongs to several picks the
 * customer there and only its sites are in play here — without this they
 * pick twice, once per level.
 *
 * Supers are exempt: `/user/UserOrgs` returns every org to them by design
 * while their only WorkOS membership is MEQ, so narrowing would strand them
 * in MEQ's sites. Any unestablished pairing (no `workosOrgId` on the
 * response, no org on the session, nothing matching) falls back to the full
 * list — the picker is the safe outcome, an empty list locks the user out.
 */
function orgsForPicker(args: {
  orgs: Org[];
  workosOrgId: string | null;
  isSuper: boolean;
}): Org[] {
  const { orgs, workosOrgId, isSuper } = args;
  if (isSuper || !workosOrgId) return orgs;
  const withinAuthenticatedOrg = orgs.filter(
    (org) => org?.workosOrgId === workosOrgId,
  );
  return withinAuthenticatedOrg.length > 0 ? withinAuthenticatedOrg : orgs;
}

async function bootWorkos(ctx: BootCtx): Promise<void> {
  const info = await fetchUserInfo();
  const synthesizedUser = synthesizeUserFromUserInfo(info);
  const profile = profileFromWorkosUserInfo(info);

  const fetchedOrgs = await fetchUserOrgs({ user: null });

  if (!fetchedOrgs || fetchedOrgs.length === 0) {
    ctx.setState({
      status: "error",
      error: new Error("No accessible orgs"),
      selectOrg: ctx.selectOrg,
    });
    return;
  }

  // The full list stays the app's org set (SwitchOrg spans customers);
  // only what the picker offers is narrowed.
  const pickerOrgs = orgsForPicker({
    orgs: fetchedOrgs,
    workosOrgId: store.get(workosAuthAtom).orgId,
    isSuper: info.super !== undefined,
  });

  if (pickerOrgs.length > 1) {
    // Stash so selectOrg can read the orgs the user picked from. The
    // config/access sequence is deferred until selectOrg
    // resolves the active org — fetching now with an ambiguous
    // current-org would mislead consumers.
    ctx.pendingOrgsRef.current = fetchedOrgs;
    // Pre-populate userAtom + authProfileAtom so SelectOrg can render
    // the user's display name without waiting for the access fetch.
    store.set(userAtom, synthesizedUser);
    store.set(authProfileAtom, profile);
    ctx.setState({
      status: "needsOrgSelection",
      orgsAwaitingSelection: pickerOrgs,
      selectOrg: ctx.selectOrg,
    });
    return;
  }

  // One candidate — auto-select it. The org set keeps every org the user can
  // reach, so an auto-selected user can still switch customers in-app.
  const firstOrg = pickerOrgs[0]!;
  const orgsWithCurrent: Org[] = fetchedOrgs.map((org) => ({
    ...org!,
    isCurrent: org!.idString === firstOrg!.idString,
  }));
  const fetchedUserConfig = await fetchUserConfig({ user: null });
  await prefetchAccess(firstOrg);

  actAfterLoginSuccess({
    orgs: orgsWithCurrent,
    user: synthesizedUser,
    userConfig: fetchedUserConfig,
  });
  store.set(authProfileAtom, profile);

  cleanupAuthQueryParams();
  ctx.setState({ status: "ready", selectOrg: ctx.selectOrg });
}

async function bootZitadel(ctx: BootCtx): Promise<void> {
  const u = store.get(userAtom);
  const zOrg = store.get(zitadelOrgAtom);
  if (!u) {
    // Login mounts the hook before Zitadel finishes hydrating
    // userAtom; the effect re-runs once `triggerReady` flips true so
    // there's nothing to do here.
    return;
  }
  const zitadelOrgIdString = zOrg?.idString;

  const fetchedOrgs = await fetchUserOrgs({
    user: u,
    zitadelOrgIdString,
  });

  if (!fetchedOrgs || fetchedOrgs.length === 0) {
    ctx.setState({
      status: "error",
      error: new Error("No accessible orgs"),
      selectOrg: ctx.selectOrg,
    });
    return;
  }

  const profile = profileFromZitadelUser(u);

  if (fetchedOrgs.length > 1) {
    ctx.pendingOrgsRef.current = fetchedOrgs;
    store.set(authProfileAtom, profile);
    ctx.setState({
      status: "needsOrgSelection",
      orgsAwaitingSelection: fetchedOrgs,
      selectOrg: ctx.selectOrg,
    });
    return;
  }

  // Single-org happy path — auto-select.
  const firstOrg = fetchedOrgs[0]!;
  const orgsWithCurrent: Org[] = [{ ...firstOrg, isCurrent: true }];
  const fetchedUserConfig = await fetchUserConfig({
    user: u,
    zitadelOrgIdString,
  });
  await prefetchAccess(firstOrg);

  actAfterLoginSuccess({
    orgs: orgsWithCurrent,
    user: u,
    userConfig: fetchedUserConfig,
  });
  store.set(authProfileAtom, profile);

  ctx.setState({ status: "ready", selectOrg: ctx.selectOrg });
}

// Export setters for testability — keep them off the public surface.
export const __test__ = {
  synthesizeUserFromUserInfo,
  cleanupAuthQueryParams,
  profileFromWorkosUserInfo,
};
